import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useSite, UI, resolveImg } from "@/lib/site";
import { MaskLine, Reveal } from "@/components/Reveal";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

const DirCard = ({ d, i }) => {
  const { t } = useSite();
  return (
    <motion.div
      className={`${SPANS[i % 4]}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: (i % 2) * 0.1 }}
    >
      <Link to={`/direction/${d.slug}`} data-testid={`direction-card-${d.slug}`} className="group relative block h-[440px] overflow-hidden rounded-[26px] bg-[#1d1712] lg:h-[560px]">
        <img src={resolveImg(d.image)} alt={t(d.title)} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform [transition-duration:1600ms] [transition-timing-function:cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.08]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10" />
        <span className="font-display absolute right-6 top-4 text-[7rem] leading-none text-white/15 transition-colors duration-700 group-hover:text-[#f0d78e]/40 md:text-[9rem]">0{i + 1}</span>
        <div className="absolute inset-x-0 bottom-0 p-7 md:p-10">
          <span className="block h-px w-12 bg-[#f0d78e] transition-[width] duration-700 group-hover:w-28" />
          <h3 className="font-display mt-5 text-5xl font-medium text-[#fbf3e2] md:text-6xl">{t(d.title)}</h3>
          <p className="mt-3 max-w-md text-[0.95rem] leading-relaxed text-[#e2d6c0]">{t(d.short)}</p>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#f0d78e]/50 px-5 py-2.5 text-sm font-semibold text-[#f0d78e] transition-colors duration-500 group-hover:bg-[#f0d78e] group-hover:text-[#1d1712]">
            {t(UI.more)} <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const Directions = () => {
  const { t, content } = useSite();
  const dirs = Array.isArray(content?.directions) ? content.directions : [];
  return (
    <section id="directions" data-testid="directions-section" className="relative bg-[#f3eadb] px-5 py-24 md:px-10 md:py-36">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">{t(UI.directions)}</p>
            <h2 className="font-display mt-6 text-4xl font-medium leading-[1.02] text-[#1d1712] sm:text-5xl lg:text-6xl">
              <MaskLine inView>{t(UI.directionsTitle)}</MaskLine>
            </h2>
          </div>
          <Reveal className="lg:col-span-5">
            <p className="text-base leading-relaxed text-[#4a3f34] md:text-lg">{t(UI.directionsLead)}</p>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-5 lg:grid-cols-12">
          {dirs.map((d, i) => <DirCard key={d.slug} d={d} i={i} />)}
        </div>
      </div>
    </section>
  );
};
